import { Select } from '../../../../common/customComponents'
import React, { useEffect, useState } from 'react'
import { StockMovementContainer } from '../../stockMovementStore'
import { MovementType, ProductSapCode, StockProduct, MovementForm } from '../../../stockModels'
import { Guid } from '@guid'
import { t } from 'i18next'
import { GetIsIn } from '../../../../deals/dealModels'

let lockedMovementTypes = [MovementType.Purchase, MovementType.Sale]

type Target = keyof Pick<MovementForm, 'originProductSapCode' | 'destinationProductSapCode'>
type TargetDependency = keyof Pick<MovementForm, 'originProductId' | 'destinationProductId'>

export function ProductSapCodeForm() {
    let store = StockMovementContainer.useContainer()

    if (store.stockMovement.movementType == null) return (<></>)

    let isIn = GetIsIn(store.stockMovement?.movementType)
    let target: Target = isIn ? 'destinationProductSapCode' : 'originProductSapCode'

    if (store.stockMovement.movementType === MovementType.Rebranding)
        return (
            <>
                <_ProductSapCode target='originProductSapCode' setBoth={false} all={store.productSapCodes} />
                <_ProductSapCode target='destinationProductSapCode' setBoth={false} all={store.productSapCodes} />
            </>)
    else
        return <_ProductSapCode target={target} setBoth={true} all={store.productSapCodes} />
}

function _ProductSapCode({ all, target, setBoth }: { all: ProductSapCode[], target: Target, setBoth: boolean }) {
    if (all.length === 0) return <></>

    let store = StockMovementContainer.useContainer()

    let [choices, setChoices] = useState<string[]>([])

    let locked = lockedMovementTypes.some(x => x === store.stockMovement.movementType) || (!!store.stockMovement.mainSapFlow?.referenceOrder || !!store.stockMovement.mainSapFlow?.materialDocument)

    let productDependency = getDependency(target)

    let setProductSapCode = (value: string) => {
        if (setBoth) {
            store.setStockMovement((prevState) => ({ ...prevState, originProductSapCode: value, destinationProductSapCode: value }))
        } else {
            store.setStockMovement((prevState) => ({ ...prevState, [target]: value }))
        }
    }

    useEffect(() => {
        let productId = store.stockMovement[productDependency]
        let productCode = getCode(store.products, productId)

        if (!store.stockMovement.company || !productCode) return

        let [productChoices, defaultChoice] = getChoicesAndDefault(all, productCode, store.stockMovement.company,
            choices => (!locked && (!choices.some(x => x.sapCode === store.stockMovement[target]))))

        if (!!defaultChoice) setProductSapCode(defaultChoice ?? null)

        if (locked) productChoices = [store.stockMovement[target] ?? '']

        setChoices(productChoices)
    }, [all, store.stockMovement.company, store.stockMovement[productDependency], store.products])

    let getStatus = store.formErrors.movementFieldStatus.getStatus

    return (
        <Select value={store.stockMovement[target]}
            choices={choices}
            label={getLabel(target, setBoth)}
            disabled={locked}
            onChange={(val) => {
                if (val) setProductSapCode(val)
            }}
            status={getStatus(target)} />)
}

let getDependency = (target: Target): TargetDependency => {
    if (target === 'originProductSapCode') return 'originProductId'
    else return 'destinationProductId'
}

let getLabel = (target: Target, setBoth: boolean) => {
    if (setBoth)
        return t('stock.label.movement.productSapCode')
    else if (target === 'originProductSapCode')
        return t('stock.label.movement.originProductSapCode')
    else
        return t('stock.label.movement.destinationProductSapCode')
}

let getCode = (products: StockProduct[], productId: Guid | null): string | undefined => products.find(x => x.id === productId)?.code

let getChoicesAndDefault = (all: ProductSapCode[], productCode: string, company: string, hasToComputeDefault: (choices: ProductSapCode[]) => boolean)
    : [string[], string | undefined] => {
    let choices: ProductSapCode[] = all.filter(x => x.productCode === productCode && company === x.companyCode)
    let defaultChoice: string | undefined = undefined

    if (hasToComputeDefault(choices)) {
        defaultChoice = choices.find(x => x.isDefault)?.sapCode
    }
    let formattedChoices = choices.map(x => x.sapCode)

    return [formattedChoices, defaultChoice]
}